import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTutorial } from '@/contexts/tutorialContext'
import { Button } from '@/components/Button'
import { Loading, Modal } from 'react-daisyui'
import { IconSchool } from '@tabler/icons-react'

const PADDING = 8

interface SpotRect {
  top: number
  left: number
  width: number
  height: number
}

export default function TutorialOverlay() {
  const activeSeries = useTutorial(s => s.activeSeries)
  const currentStepIndex = useTutorial(s => s.currentStepIndex)
  const isActive = useTutorial(s => s.isActive)
  const spotsVersion = useTutorial(s => s.spotsVersion)
  const getSpotElement = useTutorial(s => s.getSpotElement)
  const advanceStep = useTutorial(s => s.advanceStep)
  const endTutorial = useTutorial(s => s.endTutorial)

  const [spotRect, setSpotRect] = useState<SpotRect | null>(null)
  const endModalRef = useRef<HTMLDialogElement>(null)
  const animFrameRef = useRef<number>(0)
  const lastRectRef = useRef<string>('')

  const currentStep = activeSeries?.steps[currentStepIndex] ?? null

  // Continuously track spot position via RAF loop (only updates state when values change)
  useEffect(() => {
    if (!isActive || !currentStep) {
      setSpotRect(null)
      lastRectRef.current = ''
      return
    }
    let running = true
    function loop() {
      if (!running) return
      const el = getSpotElement(currentStep!.spotId)
      if (!el) {
        if (lastRectRef.current !== 'null') {
          lastRectRef.current = 'null'
          setSpotRect(null)
        }
      } else {
        const rect = el.getBoundingClientRect()
        const key = `${rect.top},${rect.left},${rect.width},${rect.height}`
        if (key !== lastRectRef.current) {
          lastRectRef.current = key
          setSpotRect({
            top: rect.top - PADDING,
            left: rect.left - PADDING,
            width: rect.width + PADDING * 2,
            height: rect.height + PADDING * 2,
          })
        }
      }
      animFrameRef.current = requestAnimationFrame(loop)
    }
    loop()
    return () => {
      running = false
      cancelAnimationFrame(animFrameRef.current)
    }
  }, [isActive, currentStep, spotsVersion, getSpotElement])

  // Scroll spot into view when step changes
  useEffect(() => {
    if (!currentStep) return
    const el = getSpotElement(currentStep.spotId)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [currentStepIndex, spotsVersion])

  // Apply z-index to the spot element so it sits above the overlay
  useEffect(() => {
    if (!currentStep) return
    const el = getSpotElement(currentStep.spotId)
    if (!el) return

    const prevZIndex = el.style.zIndex
    const prevPosition = el.style.position
    el.style.zIndex = '9999'
    if (getComputedStyle(el).position === 'static') {
      el.style.position = 'relative'
    }

    return () => {
      el.style.zIndex = prevZIndex
      el.style.position = prevPosition
    }
  }, [currentStep, spotsVersion, getSpotElement])

  // Click listener for 'click' action steps
  useEffect(() => {
    if (!currentStep || currentStep.action !== 'click') return
    const el = getSpotElement(currentStep.spotId)
    if (!el) return

    function handleClick() {
      // Defer so the click event fully propagates first (Link SPA navigation etc.)
      setTimeout(() => advanceStep(), 0)
    }

    el.addEventListener('click', handleClick, { capture: true })
    return () => {
      el.removeEventListener('click', handleClick, { capture: true })
    }
  }, [currentStep, spotsVersion, getSpotElement, advanceStep])

  // Debug log (only on step/active changes)
  useEffect(() => {
    console.log('[TutorialOverlay] isActive:', isActive, 'step:', currentStepIndex, 'spotId:', currentStep?.spotId)
  }, [isActive, currentStepIndex])

  if (!isActive || !currentStep) return null

  const isWaiting = currentStep.waitForSpot && !spotRect

  // Info box positioning: below the spot, or above if not enough space
  let infoTop = 0
  let infoLeft = 0
  const infoBoxWidth = 320
  if (spotRect) {
    infoTop = spotRect.top + spotRect.height + 12
    infoLeft = spotRect.left + spotRect.width / 2 - infoBoxWidth / 2

    // Keep within viewport
    if (infoLeft < 12) infoLeft = 12
    if (infoLeft + infoBoxWidth > window.innerWidth - 12) {
      infoLeft = window.innerWidth - infoBoxWidth - 12
    }

    // If below would go off-screen, position above
    if (infoTop + 150 > window.innerHeight) {
      infoTop = spotRect.top - 150 - 12
    }
  }

  const stepLabel = `Schritt ${currentStepIndex + 1} von ${activeSeries!.steps.length}`

  const overlay = (
    <>
      {/* Dark overlay with cutout */}
      {spotRect ? (
        <>
          {/* Top */}
          <div
            className="fixed bg-[black] bg-opacity-60 z-[9998] transition-all duration-300"
            style={{ top: 0, left: 0, right: 0, height: Math.max(0, spotRect.top) }}
          />
          {/* Bottom */}
          <div
            className="fixed bg-[black] bg-opacity-60 z-[9998] transition-all duration-300"
            style={{ top: spotRect.top + spotRect.height, left: 0, right: 0, bottom: 0 }}
          />
          {/* Left */}
          <div
            className="fixed bg-[black] bg-opacity-60 z-[9998] transition-all duration-300"
            style={{ top: spotRect.top, left: 0, width: Math.max(0, spotRect.left), height: spotRect.height }}
          />
          {/* Right */}
          <div
            className="fixed bg-[black] bg-opacity-60 z-[9998] transition-all duration-300"
            style={{ top: spotRect.top, left: spotRect.left + spotRect.width, right: 0, height: spotRect.height }}
          />
        </>
      ) : (
        // Full overlay when no spot is visible (waiting)
        <div className="fixed inset-0 bg-[black] bg-opacity-60 z-[9998]" />
      )}

      {/* Info box */}
      {isWaiting ? (
        <div className="fixed z-[10000] flex flex-col items-center gap-3" style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
          <Loading size="lg" color="primary" />
          <span className="text-[white] text-sm">Einen Moment...</span>
        </div>
      ) : spotRect ? (
        <div
          className="fixed z-[10000] bg-neutral rounded-lg shadow-xl p-4 transition-all duration-300"
          style={{ top: infoTop, left: infoLeft, width: infoBoxWidth }}
        >
          <p className="text-sm mb-3">{currentStep.text}</p>

          {currentStep.action === 'next' && (
            <Button size="sm" color="primary" className="w-full mb-2" onClick={advanceStep}>
              Weiter
            </Button>
          )}

          <div className="flex items-center justify-between text-xs opacity-50">
            <span>{stepLabel}</span>
            <button
              className="hover:opacity-80 underline"
              onClick={() => endModalRef.current?.showModal()}
            >
              Tutorial beenden
            </button>
          </div>
        </div>
      ) : null}

      {/* End tutorial confirmation modal */}
      <Modal ref={endModalRef} className="bg-neutral z-[10001]">
        <Modal.Header>
          <div className="flex flex-col items-center gap-3 pt-2">
            <div className="bg-primary bg-opacity-20 text-primary rounded-full p-4">
              <IconSchool size={36} />
            </div>
            <span className="text-xl font-bold text-center">Tutorial beenden</span>
          </div>
          <form method="dialog">
            <button className="btn btn-sm btn-circle btn-ghost absolute right-5 top-5">✕</button>
          </form>
        </Modal.Header>
        <Modal.Body>
          <div className="flex flex-col gap-4 items-center">
            <p className="text-center opacity-80">Bist du sicher, dass du das Tutorial beenden möchtest?</p>
            <div className="flex flex-row gap-3 justify-center">
              <form method="dialog">
                <Button color="ghost">Abbrechen</Button>
              </form>
              <Button
                color="primary"
                onClick={() => {
                  endModalRef.current?.close()
                  endTutorial()
                }}
              >
                Beenden
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  )

  return createPortal(overlay, document.body)
}
